/**
 * The Admin's **Cadastro de passageiro** screen — pure view-model (W9-PROFILES-REGISTRIES-KEYS, D475, ADR-0093; DevMobility
 * E-001/E-005, wave-8 finding X-09).
 *
 * An operator registers a rider who cannot (or will not) sign up alone, and an operator proposes a new phone for an account that a
 * DIFFERENT operator approves. This module holds what can be tested without React: the form normalization (the same rules the
 * server enforces, so the operator sees the problem before a round trip) and which phone-change requests the reader may decide.
 * Neutral: any E.164 number, any country code; no country, document or format is named.
 */
import { type Result } from '@vello/core';
import {
  PHONE_CHANGE_REASON_CODES, RIDER_REGISTRATION_REASON_CODES, type PhoneChangeReasonCode, type PhoneChangeRequest, type ProposePhoneChangeInput,
  type RegisterRiderInput, type RiderRegistrationReasonCode,
} from '@vello/api-client';

export { PHONE_CHANGE_REASON_CODES, RIDER_REGISTRATION_REASON_CODES };
export type { PhoneChangeReasonCode, RiderRegistrationReasonCode };

export interface RiderForm {
  readonly firstName: string;
  readonly lastName: string;
  readonly phoneE164: string;
  readonly email: string;
  readonly countryCode: string;
  readonly locale: string;
  readonly timezone: string;
  readonly note: string;
}

export interface PhoneChangeForm {
  readonly userId: string;
  readonly newPhoneE164: string;
  readonly identityChecked: boolean;
  readonly note: string;
}

export type RiderFormIssue = 'firstName' | 'lastName' | 'phone' | 'email' | 'country' | 'locale' | 'timezone';
export type PhoneChangeFormIssue = 'userId' | 'phone' | 'identity';

const E164 = /^\+[1-9]\d{6,14}$/;
const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;
const COUNTRY = /^[A-Z]{2}$/;
const LOCALE = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function emptyRiderForm(countryCode = '', locale = '', timezone = ''): RiderForm {
  return { firstName: '', lastName: '', phoneE164: '', email: '', countryCode, locale, timezone, note: '' };
}

export function emptyPhoneChangeForm(userId = ''): PhoneChangeForm {
  return { userId, newPhoneE164: '', identityChecked: false, note: '' };
}

/** Strip the spaces, dashes and parentheses a person types into a phone; the `+` and the digits stay. */
export function cleanPhone(raw: string): string {
  return raw.replace(/[\s().-]/g, '');
}

function validTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function normalizeRiderForm(form: RiderForm, reasonCode: RiderRegistrationReasonCode): Result<RegisterRiderInput, readonly RiderFormIssue[]> {
  const issues: RiderFormIssue[] = [];
  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();
  const phoneE164 = cleanPhone(form.phoneE164);
  const email = form.email.trim().toLowerCase();
  const countryCode = form.countryCode.trim().toUpperCase();
  const locale = form.locale.trim();
  const timezone = form.timezone.trim();
  if (firstName === '' || firstName.length > 80) issues.push('firstName');
  if (lastName.length > 80) issues.push('lastName');
  if (!E164.test(phoneE164)) issues.push('phone');
  if (email !== '' && !EMAIL.test(email)) issues.push('email');
  if (!COUNTRY.test(countryCode)) issues.push('country');
  if (!LOCALE.test(locale)) issues.push('locale');
  if (timezone === '' || !validTimeZone(timezone)) issues.push('timezone');
  if (issues.length > 0) return { ok: false, error: issues };
  const note = form.note.trim();
  return {
    ok: true,
    value: {
      phoneE164, countryCode, firstName, ...(lastName !== '' ? { lastName } : {}), ...(email !== '' ? { email } : {}), locale, timezone, reasonCode, ...(note !== '' ? { note } : {}),
    },
  };
}

export function normalizePhoneChangeForm(form: PhoneChangeForm, reasonCode: PhoneChangeReasonCode): Result<{ userId: string; input: ProposePhoneChangeInput }, readonly PhoneChangeFormIssue[]> {
  const issues: PhoneChangeFormIssue[] = [];
  const userId = form.userId.trim();
  const newPhoneE164 = cleanPhone(form.newPhoneE164);
  if (!UUID.test(userId)) issues.push('userId');
  if (!E164.test(newPhoneE164)) issues.push('phone');
  if (!form.identityChecked) issues.push('identity');
  if (issues.length > 0) return { ok: false, error: issues };
  const note = form.note.trim();
  return { ok: true, value: { userId, input: { newPhoneE164, reasonCode, identityChecked: true, ...(note !== '' ? { note } : {}) } } };
}

/**
 * The request a reader may approve: pending, not its own proposal (the server and the database refuse a self-approval) and — D502, Q-015 — with the
 * NEW number confirmed (the server refuses the approval of an unconfirmed one). A reader may always reject a pending request it did not propose.
 */
/** D868: `selfApproval` (a GLOBAL super administrator) approves the change they requested too; the number must still be confirmed. */
export function canDecidePhoneChange(r: PhoneChangeRequest, selfApproval = false): boolean {
  return r.state === 'PENDING' && (r.mine !== true || selfApproval) && r.numberVerifiedAt !== undefined;
}

export function canRejectPhoneChange(r: PhoneChangeRequest): boolean {
  return r.state === 'PENDING' && r.mine !== true;
}

/** D502 (Q-015): the proposer confirms the code the person read out — only while the number is not yet confirmed. */
export function canVerifyPhoneNumber(r: PhoneChangeRequest): boolean {
  return r.state === 'PENDING' && r.mine === true && r.numberVerifiedAt === undefined;
}

export function canWithdrawPhoneChange(r: PhoneChangeRequest): boolean {
  return r.state === 'PENDING' && r.mine === true;
}

/** The last four digits of a phone, for a row that must not repeat the whole number (the queue shows the whole one to the checker). */
export function phoneTail(phone: string): string {
  return phone.slice(-4);
}
